import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { HandHeart, CheckCircle, FileText, Pen, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND_NAME = "Seniors Helping Seniors® Union & Somerset County NJ";

const SignOfferPage = () => {
  const { signingToken } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    fetchOffer();
  }, [signingToken]);

  const fetchOffer = async () => {
    try {
      const response = await axios.get(`${API}/offer-letter/sign/${signingToken}`);
      setOffer(response.data);
      if (response.data.status === 'already_signed') {
        setSigned(true);
      }
    } catch (error) {
      toast.error('Offer letter not found or link has expired');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!signatureName.trim()) {
      toast.error('Please enter your full legal name');
      return;
    }
    if (!agreed) {
      toast.error('Please agree to the terms to sign');
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API}/offer-letter/sign/${signingToken}`, {
        candidate_id: '',
        signature_name: signatureName.trim(),
        agreed: true
      });
      setSigned(true);
      toast.success('Offer letter signed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign offer letter');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#4B2D6E] border-t-transparent mx-auto mb-4"></div>
          <p className="text-[#666666]">Loading offer letter...</p>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <header className="bg-[#4B2D6E] text-white py-4 px-6">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <HandHeart className="h-8 w-8 text-[#E8722A]" />
            <h1 className="font-bold font-[Lato]">{BRAND_NAME}</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-12">
          <Card className="border-t-4 border-red-500">
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#4B2D6E] mb-2">Link Not Found</h2>
              <p className="text-[#666666]">
                This offer letter link is invalid or has expired. Please contact us if you believe this is an error.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (signed || offer.status === 'already_signed') {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <header className="bg-[#4B2D6E] text-white py-4 px-6">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <HandHeart className="h-8 w-8 text-[#E8722A]" />
            <h1 className="font-bold font-[Lato]">{BRAND_NAME}</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-12">
          <Card className="border-t-4 border-[#4CAF50]">
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-20 w-20 text-[#4CAF50] mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-[#4B2D6E] mb-4 font-[Lato]">Thank You!</h2>
              <p className="text-lg text-[#666666] mb-6">
                Your signed offer letter has been received. We're excited to have you join our team!
              </p>
              <p className="text-sm text-[#999999]">
                You will receive a confirmation email shortly with next steps for onboarding.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <header className="bg-[#4B2D6E] text-white py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <HandHeart className="h-8 w-8 text-[#E8722A]" />
          <div>
            <h1 className="font-bold font-[Lato]">{BRAND_NAME}</h1>
            <p className="text-sm text-gray-300">Offer Letter for {offer.candidate_name}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Offer Letter Content */}
        <Card className="border-t-4 border-[#4B2D6E]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#4B2D6E]">
              <FileText className="h-5 w-5" />
              Your Offer Letter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white border rounded-lg p-6 md:p-8 whitespace-pre-wrap font-serif text-[#333333] leading-relaxed">
              {offer.letter_content}
            </div>
            <p className="text-sm text-[#666666] mt-4">
              Sent on: {new Date(offer.sent_at).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="border-t-4 border-[#E8722A]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#4B2D6E]">
              <Pen className="h-5 w-5" />
              Sign to Accept
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="signature" className="text-base">
                Your Full Legal Name (as signature)
              </Label>
              <Input
                id="signature"
                type="text"
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Enter your full legal name"
                className="h-14 text-lg border-2 focus:border-[#4B2D6E] font-serif"
                data-testid="signature-input"
              />
            </div>

            <div className="flex items-start space-x-3 p-4 bg-[#F5F5F5] rounded-lg">
              <Checkbox
                id="agree"
                checked={agreed}
                onCheckedChange={setAgreed}
                className="mt-1 border-[#4B2D6E] data-[state=checked]:bg-[#4B2D6E]"
                data-testid="agree-checkbox"
              />
              <Label htmlFor="agree" className="text-sm leading-relaxed cursor-pointer">
                I have read and agree to the terms of this offer letter. I understand that my typed name above 
                constitutes my legal electronic signature.
              </Label>
            </div>

            <Button
              onClick={handleSign}
              disabled={signing || !signatureName.trim() || !agreed}
              className="w-full h-14 bg-[#E8722A] hover:bg-[#D6621F] text-white text-lg font-bold rounded-full"
              data-testid="sign-btn"
            >
              {signing ? 'Signing...' : 'Sign & Submit'}
            </Button>

            <p className="text-xs text-center text-[#999999]">
              By signing, you agree that your electronic signature is legally binding.
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-[#4B2D6E] text-white py-6 px-6 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-300 text-sm">
            © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SignOfferPage;
